/**
 * Cookie utilities for managing JWT token storage
 * Uses modern Cookie Store API with fallback to document.cookie
 */

import { AUTH_TOKEN_COOKIE_NAME } from "./types"

/**
 * Get cookie value by name using Cookie Store API with fallback
 * Handles SSR environment gracefully
 * @param name - Cookie name
 * @returns Cookie value or null if not found
 */
export async function getCookie(name: string): Promise<string | null> {
	if (typeof window === "undefined") {
		// SSR environment - cookie not available
		return null
	}

	// Try to use modern Cookie Store API first
	if (typeof cookieStore !== "undefined") {
		try {
			const cookie = await cookieStore.get(name)
			return cookie?.value ?? null
		} catch {
			// Fall back to document.cookie if Cookie Store API fails
		}
	}

	// Fallback to document.cookie API
	if (typeof document === "undefined") {
		return null
	}

	const cookies = document.cookie.split("; ")
	for (const cookie of cookies) {
		const [cookieName, cookieValue] = cookie.split("=")
		if (cookieName === name) {
			return cookieValue ? decodeURIComponent(cookieValue) : null
		}
	}

	return null
}

/**
 * Delete cookie by name using Cookie Store API with fallback
 * Handles SSR environment gracefully
 * @param name - Cookie name
 * @param path - Cookie path (default: '/')
 */
export async function deleteCookie(
	name: string,
	path: string = "/",
): Promise<void> {
	if (typeof window === "undefined") {
		// SSR environment - cookie not available
		return Promise.resolve()
	}

	// Try to use modern Cookie Store API first
	if (typeof cookieStore !== "undefined") {
		try {
			await cookieStore.delete({ name, path })
			return
		} catch {
			// Fall back to document.cookie if Cookie Store API fails
		}
	}

	// Fallback to document.cookie API
	if (typeof document !== "undefined") {
		// biome-ignore lint/suspicious/noDocumentCookie: fallback logic should allow manipulating cookies directly
		document.cookie = `${name}=; Path=${path}; Max-Age=0`
	}
}

/**
 * Get JWT token from cookie
 * @returns Promise resolving to JWT token or null if not found
 */
export async function getAuthToken(): Promise<string | null> {
	return await getCookie(AUTH_TOKEN_COOKIE_NAME)
}

/**
 * Delete JWT token from cookie
 */
export async function deleteAuthToken(): Promise<void> {
	await deleteCookie(AUTH_TOKEN_COOKIE_NAME)
}
